import type {
  AuditEntry,
  Change,
  ChangesPerDayPoint,
  DashboardStats,
  Execution,
  Organization,
  OrgConfig,
  OrgConfigPatch,
  OrgMember,
  TimeWindow,
} from '../types/domain';
import { supabase } from './supabase';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }

  static async from(res: Response): Promise<ApiError> {
    let message = res.statusText;
    try {
      const body = (await res.json()) as { message?: string; error?: string };
      message = body.message ?? body.error ?? message;
    } catch {
      // use statusText
    }
    return new ApiError(res.status, message);
  }
}

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

async function authedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) throw new ApiError(401, 'Not authenticated');

  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      // Solo cuando hay cuerpo: declarar application/json en un DELETE o PATCH
      // sin body hace que Fastify rechace con 400 FST_ERR_CTP_EMPTY_JSON_BODY
      // antes de llegar al handler.
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init.headers as Record<string, string> | undefined),
      Authorization: `Bearer ${session.access_token}`,
    },
  });

  if (!res.ok) throw await ApiError.from(res);
  return res;
}

function qs(params: Record<string, string | number | boolean | undefined>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined) p.set(k, String(v));
  }
  return p.toString();
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
async function _j<T>(res: Response): Promise<T> {
  return res.json() as Promise<T>;
}

// Unwraps { success, data } envelope returned by all admin API routes
async function jd<T>(res: Response): Promise<T> {
  const body = (await res.json()) as { data: T };
  return body.data;
}

export const adminApi = {
  organizations: {
    list: (): Promise<Organization[]> => authedFetch('/api/admin/organizations').then((r) => jd(r)),

    get: (id: string): Promise<Organization> =>
      authedFetch(`/api/admin/organizations/${id}`).then((r) => jd(r)),

    create: (body: {
      name: string;
      type: 'individual' | 'team';
      identifier: string;
      email: string;
    }): Promise<Organization> =>
      authedFetch('/api/admin/organizations', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => jd(r)),

    disable: (id: string): Promise<Organization> =>
      authedFetch(`/api/admin/organizations/${id}/disable`, { method: 'PATCH' }).then((r) => jd(r)),

    enable: (id: string): Promise<Organization> =>
      authedFetch(`/api/admin/organizations/${id}/enable`, { method: 'PATCH' }).then((r) => jd(r)),

    run: (id: string): Promise<{ queued: boolean }> =>
      authedFetch(`/api/admin/organizations/${id}/run`, { method: 'POST' }).then((r) => jd(r)),

    listMembers: (id: string): Promise<OrgMember[]> =>
      authedFetch(`/api/admin/organizations/${id}/members`).then((r) => jd(r)),

    assignMember: (
      id: string,
      body: { user_id: string; role: 'owner' | 'member' }
    ): Promise<OrgMember> =>
      authedFetch(`/api/admin/organizations/${id}/members`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => jd(r)),

    removeMember: (id: string, userId: string): Promise<void> =>
      authedFetch(`/api/admin/organizations/${id}/members/${userId}`, {
        method: 'DELETE',
      }).then(() => undefined),

    getConfig: (id: string): Promise<OrgConfig> =>
      authedFetch(`/api/admin/organizations/${id}/config`).then((r) => jd<OrgConfig>(r)),

    updateConfig: (id: string, patch: OrgConfigPatch): Promise<OrgConfig> =>
      authedFetch(`/api/admin/organizations/${id}/config`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      }).then((r) => jd<OrgConfig>(r)),
  },

  users: {
    list: (params?: {
      page?: number;
      per_page?: number;
    }): Promise<{ users: import('../types/domain').AdminUser[]; total: number }> =>
      authedFetch(`/api/admin/users?${qs(params ?? {})}`).then((r) => jd(r)),

    get: (
      id: string
    ): Promise<{
      user: import('../types/domain').AdminUser;
      memberships: Array<{ org_id: string; org_name: string; role: string }>;
    }> => authedFetch(`/api/admin/users/${id}`).then((r) => jd(r)),
  },

  impersonate: (userId: string): Promise<{ link: string }> =>
    authedFetch('/api/admin/impersonate', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId }),
    }).then((r) => jd(r)),

  dashboard: {
    stats: (): Promise<DashboardStats> =>
      authedFetch('/api/admin/dashboard/stats').then((r) => jd(r)),

    executions: (params?: { org_id?: string; limit?: number }): Promise<Execution[]> =>
      authedFetch(`/api/admin/dashboard/executions?${qs(params ?? {})}`).then((r) => jd(r)),

    changes: (params?: { org_id?: string; limit?: number }): Promise<Change[]> =>
      authedFetch(`/api/admin/dashboard/changes?${qs(params ?? {})}`).then((r) => jd(r)),

    changesPerDay: (days: TimeWindow, orgId?: string): Promise<ChangesPerDayPoint[]> =>
      authedFetch(
        `/api/admin/dashboard/changes-per-day?days=${days}${orgId ? `&org_id=${orgId}` : ''}`
      ).then((r) => jd<ChangesPerDayPoint[]>(r)),
  },

  auditLog: {
    list: (params?: { page?: number; per_page?: number; action?: string }): Promise<AuditEntry[]> =>
      authedFetch(`/api/admin/audit-log?${qs(params ?? {})}`).then((r) => jd(r)),
  },
};
